// src/components/character_editor/sections/TemplateEditSection.jsx
import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert
} from '@mui/material';
import {
  Build as BuildIcon,
  Psychology as PsychologyIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon
} from '@mui/icons-material';

const TemplateEditSection = ({
  character,
  templates,
  selectedTemplate,
  selectedRace,
  onTemplateChange,
  onTemperamentChange
}) => {
  // Совместимые шаблоны для выбранной расы
  const compatibleTemplates = selectedRace 
    ? templates.filter(template => 
        !template.race_compatibility || 
        template.race_compatibility.includes(selectedRace.id)
      )
    : templates;

  // Данные о темпераментах
  const temperamentsData = {
    sanguine: { name: 'Сангвиник', description: 'Общительный, активный, оптимистичный' },
    choleric: { name: 'Холерик', description: 'Решительный, целеустремленный, лидер' },
    phlegmatic: { name: 'Флегматик', description: 'Спокойный, надежный, методичный' },
    melancholic: { name: 'Меланхолик', description: 'Аналитичный, чуткий, перфекционист' },
    adaptive: { name: 'Адаптивный', description: 'Гибкий, изменчивый, многогранный' }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <BuildIcon sx={{ mr: 2 }} />
        Профессия и характер
      </Typography>

      <Grid container spacing={3}>
        {/* Выбор шаблона */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Профессия (Шаблон)
              </Typography>
              
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Выберите шаблон</InputLabel>
                <Select
                  value={character.template || ''}
                  onChange={(e) => onTemplateChange(e.target.value)}
                  label="Выберите шаблон"
                >
                  {compatibleTemplates.map(template => (
                    <MenuItem key={template.id} value={template.id}>
                      <Box sx={{ width: '100%' }}>
                        <Typography variant="body1" fontWeight="bold">
                          {template.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {template.description}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {selectedTemplate && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Основные навыки шаблона:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selectedTemplate.primary_skills?.map(skill => (
                      <Chip
                        key={skill}
                        label={skill}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {!selectedRace && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Сначала выберите расу для просмотра совместимых шаблонов
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Выбор темперамента */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <PsychologyIcon sx={{ mr: 1 }} />
                Темперамент
              </Typography>
              
              <FormControl fullWidth>
                <InputLabel>Выберите темперамент</InputLabel>
                <Select
                  value={character.temperament || ''}
                  onChange={(e) => onTemperamentChange(e.target.value)}
                  label="Выберите темперамент"
                >
                  {Object.entries(temperamentsData).map(([key, data]) => (
                    <MenuItem key={key} value={key}>
                      <Box>
                        <Typography variant="body1">{data.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {data.description}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {character.temperament && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  {temperamentsData[character.temperament]?.description}
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Информация о выбранном шаблоне */}
        {selectedTemplate && (
          <Grid item xs={12}>
            <Card elevation={1} sx={{ bgcolor: 'success.light' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <CheckCircleIcon sx={{ mr: 1 }} />
                  Текущий шаблон: {selectedTemplate.name}
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body1" paragraph>
                      {selectedTemplate.description}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom>
                      Цели персонажа:
                    </Typography>
                    <List dense>
                      {selectedTemplate.goals?.map((goal, index) => (
                        <ListItem key={index}>
                          <ListItemIcon>
                            <WarningIcon color="primary" />
                          </ListItemIcon>
                          <ListItemText primary={goal} />
                        </ListItem>
                      ))}
                    </List>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default TemplateEditSection;